"use client";

import type { SupDataGridColumn, SupSingleVaultV3 } from "@/types";
import React, { Fragment } from "react";

import PortfolioCommonBadge from "@/components/Portfolio/Common/Badge";
import LoaderInPage from "@/components/Loader/InPage";
import GridEmptyState from "@/components/Grid/EmptyState";
import PortfolioSuperpositionsTable from "@/components/Portfolio/Superpositions/Table";
import PortfolioSuperpositionsTokenList from "@/components/Portfolio/Superpositions/TokenList";
import PortfolioSuperpositionsSummary from "@/components/Portfolio/Superpositions/Summary";

import dynamic from "next/dynamic";
import useFilterSuperpositions from "@/hooks/data/useFilterSuperpositions";

const GridBarPanel = dynamic(() => import("@/components/Grid/Bar/Panel"), { ssr: false, loading: () => <div className="sup-filter-bar" /> });

const columns = [
  {
    field: "favorite",
    name: "Favorite",
    hiddenByDefault: true,
  },
  {
    field: "id",
    name: "Vault ID",
    hiddenByDefault: true,
  },
  {
    name: "Protocol",
    field: "protocol",
  },
  {
    name: "TVL",
    field: "tvl",
  },
  {
    name: "Yield Type",
    field: "yield_type",
    hiddenByDefault: true,
  },
  {
    name: "Portfolio %",
    field: "percentage",
  },
  {
    name: "24H APY",
    field: "apy_day",
    hiddenByDefault: true,
  },
  {
    name: "7D APY",
    field: "apy_week",
    hiddenByDefault: true,
  },
  {
    name: "30D APY",
    field: "apy",
  },
  {
    name: "Vault's Age",
    field: "created_at",
    hiddenByDefault: true,
  },
  {
    name: "Sharpe",
    field: "sharpe",
    hiddenByDefault: true,
  },
  {
    name: "Availability",
    field: "availability",
    hiddenByDefault: true,
  },
] as SupDataGridColumn[];

interface PortfolioMainProps {
  showSummary?: boolean;
  filterVaults?: SupSingleVaultV3[];
  hideColumns?: string[];
  isProtocol?: boolean;
  showRewards?: boolean;
}

export default function PortfolioMain({ filterVaults, hideColumns, isProtocol = false, showSummary = false }: PortfolioMainProps) {
  const { superPositions, isLoading, isError, portfolioValue, portfolioApy, isFiltered, rawSuperPositions } = useFilterSuperpositions(
    filterVaults,
    isProtocol ? !!filterVaults && filterVaults.length > 0 : true
  );

  return isLoading ? (
    <LoaderInPage />
  ) : (
    <Fragment>
      {showSummary && (
        <PortfolioSuperpositionsSummary
          label="Portfolio Summary"
          badgeChildren={
            <Fragment>
              <PortfolioCommonBadge type="fiat" value={portfolioValue} caption="Portfolio value" />
              <PortfolioCommonBadge type="apy" value={portfolioApy} caption="Average APY" />
            </Fragment>
          }
        >
          <PortfolioSuperpositionsTokenList superPositions={rawSuperPositions} portfolioValue={portfolioValue} isLoading={isLoading} />
        </PortfolioSuperpositionsSummary>
      )}

      <GridBarPanel showDust isDisabled={isLoading || isError} columns={columns} gridName="Portfolio" />

      <div className="sup-grid-container-page">
        {isError && <GridEmptyState message="api returned an error" isError />}
        {!isError && (
          <PortfolioSuperpositionsTable
            controlledColumns={columns}
            superPositions={superPositions}
            isLoading={isLoading}
            isFiltered={isFiltered}
            hideColumns={hideColumns}
            portfolioValue={portfolioValue}
          />
        )}
      </div>
    </Fragment>
  );
}
